import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import * as XLSX from "xlsx";
import axios from "axios";

const Step3 = ({ rfq, setStep, items, setItems, onFinish }) => {
  const units = ["KGS", "CBM", "Shipment", "Invoice", "Vehicle", "Container"];
  const storedUser = JSON.parse(localStorage.getItem("user")) || {};
  const fileInputRef = useRef(null);
  const [btnLoading, setBtnLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  const formatNumber = (value) =>
    new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(parseFloat(value || 0));

  useEffect(() => {
    const fetchWorkItems = async () => {
      if (!rfq.logisticDraftId) return;
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/logistics-work-items/${rfq.logisticDraftId}`,
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
        );
        if (res.data && res.data.length > 0) {
          setItems(
            res.data
              .sort((a, b) => a.itemOrder - b.itemOrder)
              .map((item, i) => ({
                srNo: i + 1,
                particulars: item.particulars || "",
                quantity: item.quantity || 0,
                unit: item.unit || "",
                currency: item.currency || "",
                newRate: item.newRate || 0,
                exchangeRate: item.exchangeRate || 0,
                total: item.total || 0,
              })),
          );
        }
      } catch (error) {
        console.error("Fetch error:", error.message);
      }
    };
    fetchWorkItems();
  }, [rfq.logisticDraftId]);

  const parsedCurrencies = useMemo(() => {
    if (!rfq.currencies) return [];
    if (typeof rfq.currencies === "string") {
      try {
        return JSON.parse(rfq.currencies);
      } catch {
        return [];
      }
    }
    return Array.isArray(rfq.currencies) ? rfq.currencies : [];
  }, [rfq.currencies]);

  const selectedCurrencies = parsedCurrencies.map((c) => c.name);

  useEffect(() => {
    const updated = items.map((item) => ({
      ...item,
      total:
        (parseFloat(item.quantity) || 0) *
        (parseFloat(item.newRate) || 0) *
        (parseFloat(item.exchangeRate) || 0),
    }));
    if (JSON.stringify(updated) !== JSON.stringify(items)) setItems(updated);
  }, [items, setItems]);

  const grandTotal = items
    .reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0)
    .toFixed(2);

  const currencyTotals = items.reduce((acc, item) => {
    const currencyName = item.currency || "";
    if (!currencyName) return acc;

    const amount =
      (parseFloat(item.quantity) || 0) * (parseFloat(item.newRate) || 0);

    acc[currencyName] = (acc[currencyName] || 0) + amount;
    return acc;
  }, {});

  const handleChange = (index, field, value) => {
    const newItems = [...items];
    if (field === "currency") {
      newItems[index].currency = value;
      if (value === "INR - Indian Rupee") {
        newItems[index].exchangeRate = 1;
      } else {
        const c = parsedCurrencies.find((c) => c.name === value);
        newItems[index].exchangeRate = c ? parseFloat(c.rate) : 0;
      }
    } else {
      newItems[index][field] = value;
    }
    setItems(newItems);
  };

  const addRow = () => {
    setItems([
      ...items,
      {
        particulars: "",
        quantity: "",
        unit: "",
        currency: "",
        newRate: 0,
        exchangeRate: 0,
        total: 0,
      },
    ]);
  };

  const deleteRow = (index) => setItems(items.filter((_, i) => i !== index));

  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet([
      {
        Particulars: "",
        Quantity: "",
        Unit: "",
        Currency: "",
        Rate: "",
      },
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, "LogisticWorkItems_Template.xlsx");
  };

  const importFromExcel = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsImporting(true);

    const reader = new FileReader();

    reader.onload = (e) => {
      const wb = XLSX.read(e.target.result, { type: "array" });
      const data = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]);

      const newItems = data.map((row, i) => {
        const currency = row.Currency || "";

        let exchangeRate = 0;

        if (currency === "INR - Indian Rupee") {
          exchangeRate = 1;
        } else {
          const c = parsedCurrencies.find((c) => {
            const code = c.name.split(" - ")[0];
            return code.toUpperCase() === currency.toUpperCase();
          });
          exchangeRate = c ? parseFloat(c.rate) : 0;
        }

        return {
          srNo: i + 1,
          particulars: row.Particulars || "",
          quantity: parseFloat(row.Quantity) || 0,
          unit: row.Unit || "",
          currency,
          newRate: parseFloat(row.Rate) || 0,
          exchangeRate,
          total: 0,
        };
      });

      setItems(newItems);

      setIsImporting(false);
      event.target.value = null;
    };

    reader.readAsArrayBuffer(file);
  };

  const handleNextClick = async () => {
    const filtered = items.filter(
      (item) => item.particulars?.toString().trim() !== "",
    );
    if (filtered.length === 0) {
      alert("Please add at least one work item with a description.");
      return;
    }
    const workItems = filtered.map((item, index) => ({
      particulars: item.particulars,
      quantity: parseFloat(item.quantity) || 0,
      unit: item.unit,
      currency: item.currency,
      newRate: parseFloat(item.newRate) || 0,
      exchangeRate: parseFloat(item.exchangeRate) || 0,
      total: parseFloat(item.total) || 0,
      itemOrder: index + 1,
    }));
    setBtnLoading(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/logistics-work-items/${rfq.logisticDraftId}`,
        workItems,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );
      onFinish({
        ...rfq,
        currencies: parsedCurrencies,
        workItems,
        status: "DRAFT",
      });
    } catch (error) {
      const msg = error.response?.data;
      alert(
        `Error: ${typeof msg === "string" ? msg : msg?.message || "Check backend."}`,
      );
    } finally {
      setBtnLoading(false);
    }
  };

  const dropdownSx = {
    "& .MuiInputBase-root": { minHeight: "45px", fontSize: "12px" },
    "& input": { padding: "15px" },
  };

  const paperSx = {
    sx: {
      background: "#D8E9F0",
      "& .MuiAutocomplete-option": { fontSize: "12px", padding: "6px 12px" },
    },
  };

  return (
    <div style={{ position: "relative" }}>
      {isImporting && (
        <div className="uploading-div loading-div">
          Uploading Sheet... <CircularProgress color="#fff" />
        </div>
      )}
      {btnLoading && <div className="loading-div"></div>}

      <h2>Work Items Details</h2>

      {selectedCurrencies.length === 0 && (
        <div
          style={{
            marginBottom: "15px",
            padding: "10px 15px",
            backgroundColor: "#fff3cd",
            borderRadius: "8px",
            border: "1px solid #ffc107",
            fontSize: "13px",
          }}
        >
          ⚠️ No currencies found. Please go back to Step 2 and add at least one
          currency.
        </div>
      )}

      <div className="dashboard-container">
        <table
          border={1}
          style={{
            borderCollapse: "collapse",
            Width: "100%",
            marginTop: "10px",
          }}
        >
          <thead>
            <tr>
              <th style={{ padding: "20px", width: "60px" }}>Sr.</th>
              <th style={{ width: "25%" }}>Particulars</th>
              <th style={{ width: "12%" }}>UOM</th>
              <th style={{ width: "8%" }}>Quantity</th>
              <th style={{ width: "15%" }}>Currency</th>
              <th style={{ width: "10%" }}>Rate</th>
              <th style={{ width: "8%" }}>Ex. Rate</th>
              <th style={{ width: "10%" }}>Total (in ₹)</th>
              <th style={{ width: "60px" }}></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr key={index}>
                <td style={{ textAlign: "center", padding: "10px" }}>
                  {index + 1}
                </td>

                <td>
                  <input
                    className="table-input"
                    type="text"
                    value={item.particulars}
                    onChange={(e) =>
                      handleChange(index, "particulars", e.target.value)
                    }
                    maxLength={1000}
                  />
                </td>

                <td>
                  <Autocomplete
                    className="unit-dropdown"
                    options={units}
                    value={item.unit || null}
                    onChange={(e, v) => handleChange(index, "unit", v)}
                    disablePortal
                    size="small"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Choose Unit"
                        variant="outlined"
                        size="small"
                        sx={dropdownSx}
                      />
                    )}
                    disableClearable
                    sx={{ width: "100%" }}
                    slotProps={{ paper: paperSx }}
                  />
                </td>

                <td>
                  <input
                    className="table-input"
                    type="number"
                    value={item.quantity}
                    style={{ width: "100%" }}
                    onChange={(e) =>
                      handleChange(index, "quantity", e.target.value)
                    }
                    step={0.01}
                  />
                </td>

                <td>
                  <Autocomplete
                    className="unit-dropdown"
                    options={selectedCurrencies}
                    value={item.currency || null}
                    onChange={(e, v) => handleChange(index, "currency", v)}
                    disablePortal
                    size="small"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Currency"
                        variant="outlined"
                        size="small"
                        sx={dropdownSx}
                      />
                    )}
                    disableClearable
                    sx={{ width: "100%" }}
                    slotProps={{ paper: paperSx }}
                  />
                </td>

                <td>
                  <input
                    className="table-input"
                    type="number"
                    value={item.newRate}
                    onChange={(e) =>
                      handleChange(index, "newRate", e.target.value)
                    }
                  />
                </td>

                <td style={{ textAlign: "center", padding: "10px" }}>
                  {formatNumber(item.exchangeRate)}
                </td>

                <td style={{ textAlign: "center" }}>
                  {formatNumber(item.total)}
                </td>

                <td>
                  <Button
                    className="btn-contained"
                    onClick={() => deleteRow(index)}
                    sx={{
                      margin: "0 !important",
                      borderRadius: "5px !important",
                      minWidth: "50px !important",
                      width: "0",
                      "&:hover": { background: "#EB2B0B !important" },
                    }}
                  >
                    <ClearIcon />
                  </Button>
                </td>
              </tr>
            ))}

            {Object.entries(currencyTotals).map(([currencyName, total]) => (
              <tr
                key={`currency-total-${currencyName}`}
                style={{ backgroundColor: "#d8e9f0", fontWeight: "bold" }}
              >
                <td
                  colSpan={7}
                  style={{ textAlign: "right", paddingRight: "10px" }}
                >
                  {`Grand Total (${currencyName})`}
                </td>
                <td
                  style={{
                    width: "150px",
                    textAlign: "center",
                    padding: "20px",
                  }}
                >
                  {formatNumber(total)}
                </td>
                <td></td>
              </tr>
            ))}

            <tr style={{ backgroundColor: "#d8e9f0", fontWeight: "bold" }}>
              <td
                colSpan={7}
                style={{ textAlign: "right", paddingRight: "10px" }}
              >
                Grand Total (in ₹):
              </td>
              <td
                style={{ width: "150px", textAlign: "center", padding: "20px" }}
              >
                {formatNumber(grandTotal)}
              </td>
              <td></td>
            </tr>
          </tbody>
        </table>

        <Typography variant="p" fontWeight={700} fontSize={"14px"}>
          [Important Note: <br />
          - Uploading Sheet will overwrite existing work items. <br />- Please
          ensure that the Description field contains less than 1000 characters.
          Any additional characters beyond this limit will be automatically
          truncated.]
        </Typography>

        <div
          style={{
            display: "flex",
            justifyContent: "end",
            alignItems: "center",
            gap: 10,
          }}
        >
          <Button
            className="btn-outlined"
            onClick={() => fileInputRef.current.click()}
          >
            Upload Excel
          </Button>
          <Button
            className="btn-outlined"
            onClick={addRow}
            sx={{ padding: "5px 40px !important" }}
          >
            Add Work Item
          </Button>
        </div>

        <div style={{ display: "flex", justifyContent: "end" }}>
          <button
            onClick={exportToExcel}
            style={{
              cursor: "pointer",
              border: "none",
              background: "none",
              borderBottom: "1px solid #000",
            }}
          >
            Download Template Excel
          </button>
        </div>
      </div>

      <input
        type="file"
        accept=".xlsx, .xls"
        ref={fileInputRef}
        onChange={importFromExcel}
        style={{ display: "none" }}
      />

      <div style={{ display: "flex", justifyContent: "end" }}>
        <div
          className="btn-container"
          style={{ gap: 20, margin: "0", justifyContent: "end" }}
        >
          <Button className="btn-outlined" onClick={() => setStep(2)}>
            Back
          </Button>
          <Button
            className="btn-contained"
            onClick={handleNextClick}
            disabled={btnLoading}
          >
            {btnLoading ? "Submitting..." : "Next"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Step3;
